import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Sparkles, TrendingUp, Calendar, Target, Loader2, RefreshCw } from 'lucide-react';
import { useProductBundles } from '@/hooks/useProductBundles';
import { useBundleConversionStats } from '@/hooks/useBundleConversion';
import { useBundleEffectiveness } from '@/hooks/useBundleEffectiveness';
import { supabase } from '@/integrations/supabase/client';
import { useQuery } from '@tanstack/react-query';
import { getMonth } from 'date-fns';
import { toast } from 'sonner';

const SEASONS = [
  { name: 'Spring', months: [2, 3, 4] },
  { name: 'Summer', months: [5, 6, 7] },
  { name: 'Fall', months: [8, 9, 10] },
  { name: 'Winter', months: [11, 0, 1] },
];

function getCurrentSeason() {
  const currentMonth = getMonth(new Date());
  return SEASONS.find(s => s.months.includes(currentMonth))?.name || 'Unknown';
}

interface PromotionRecommendation {
  bundle_id: string;
  bundle_name: string;
  score: number;
  reasons: string[];
  promotion_type: 'seasonal' | 'high_performer' | 'underutilized' | 'trending';
}

export function BundlePromotionEngine() {
  const { data: bundles } = useProductBundles();
  const { data: conversionStats } = useBundleConversionStats();
  const { data: effectivenessScores } = useBundleEffectiveness();
  const [isGenerating, setIsGenerating] = useState(false);
  const [aiRecommendations, setAiRecommendations] = useState<string | null>(null);

  const currentSeason = getCurrentSeason();

  // Calculate local recommendations based on data
  const recommendations = useQuery({
    queryKey: ['bundle-promotion-recommendations', conversionStats, effectivenessScores],
    queryFn: () => {
      if (!bundles || !conversionStats || !effectivenessScores) return [];

      const recs: PromotionRecommendation[] = [];

      bundles.forEach(bundle => {
        const conversion = conversionStats.find(c => c.bundle_id === bundle.id);
        const effectiveness = effectivenessScores.find(e => e.bundle_id === bundle.id);
        const reasons: string[] = [];
        let score = 50;
        let promotion_type: PromotionRecommendation['promotion_type'] = 'underutilized';

        // High conversion rate
        if (conversion && conversion.conversion_rate > 60) {
          score += 20;
          reasons.push(`High conversion rate (${conversion.conversion_rate.toFixed(0)}%)`);
          promotion_type = 'high_performer';
        }

        // High effectiveness score
        if (effectiveness && effectiveness.effectiveness_score > 70) {
          score += 15;
          reasons.push(`Strong effectiveness score (${effectiveness.effectiveness_score.toFixed(0)})`);
          if (promotion_type !== 'high_performer') promotion_type = 'trending';
        }

        // Good revenue generator
        if (conversion && conversion.total_won_revenue > 50000) {
          score += 15;
          reasons.push(`Proven revenue generator ($${(conversion.total_won_revenue / 1000).toFixed(0)}K)`);
        }

        // Underutilized with good potential
        if (conversion && conversion.total_deals < 5 && bundle.is_active) {
          score += 10;
          reasons.push('Underutilized - opportunity for promotion');
          promotion_type = 'underutilized';
        }

        // Active bundle bonus
        if (bundle.is_active) {
          score += 5;
        }

        if (reasons.length > 0) {
          recs.push({
            bundle_id: bundle.id,
            bundle_name: bundle.name,
            score: Math.min(score, 100),
            reasons,
            promotion_type,
          });
        }
      });

      return recs.sort((a, b) => b.score - a.score).slice(0, 5);
    },
    enabled: !!bundles && !!conversionStats && !!effectivenessScores,
  });

  const generateAIRecommendations = async () => {
    if (!bundles || !conversionStats) return;

    setIsGenerating(true);
    try {
      const bundleData = bundles.map(b => {
        const conv = conversionStats.find(c => c.bundle_id === b.id);
        return {
          name: b.name,
          price: b.bundle_price,
          conversion_rate: conv?.conversion_rate || 0,
          total_deals: conv?.total_deals || 0,
          revenue: conv?.total_won_revenue || 0,
        };
      });

      const { data, error } = await supabase.functions.invoke('ai-assistant', {
        body: {
          type: 'bundle_promotions',
          context: {
            currentSeason,
            bundles: JSON.stringify(bundleData),
          },
        },
      });

      if (error) throw error;
      setAiRecommendations(data.content);
      toast.success('AI recommendations generated');
    } catch (error) {
      console.error('Error generating AI recommendations:', error);
      toast.error('Failed to generate AI recommendations');
    } finally {
      setIsGenerating(false);
    }
  };

  const getPromotionTypeColor = (type: PromotionRecommendation['promotion_type']) => {
    switch (type) {
      case 'high_performer': return 'bg-green-500';
      case 'trending': return 'bg-blue-500';
      case 'seasonal': return 'bg-orange-500';
      case 'underutilized': return 'bg-purple-500';
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-primary" />
            <CardTitle>Bundle Promotion Engine</CardTitle>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant="outline">
              <Calendar className="h-3 w-3 mr-1" />
              {currentSeason}
            </Badge>
            <Button 
              size="sm" 
              onClick={generateAIRecommendations}
              disabled={isGenerating}
            >
              {isGenerating ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <Sparkles className="h-4 w-4 mr-2" />
              )}
              AI Analysis
            </Button>
          </div>
        </div>
        <CardDescription>
          Smart recommendations for which bundles to promote based on performance and seasonality
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Data-Driven Recommendations */}
        <div className="space-y-3">
          <h4 className="font-semibold flex items-center gap-2">
            <Target className="h-4 w-4" />
            Recommended for Promotion
          </h4>
          <div className="grid gap-3">
            {recommendations.data?.map((rec, index) => (
              <div 
                key={rec.bundle_id} 
                className="flex items-start justify-between p-4 rounded-lg bg-muted/50 border"
              >
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <span className="text-lg font-bold text-muted-foreground">#{index + 1}</span>
                    <span className="font-semibold">{rec.bundle_name}</span>
                    <Badge className={getPromotionTypeColor(rec.promotion_type)}>
                      {rec.promotion_type.replace('_', ' ')}
                    </Badge>
                  </div>
                  <ul className="text-sm text-muted-foreground space-y-1">
                    {rec.reasons.map((reason, i) => (
                      <li key={i} className="flex items-center gap-2">
                        <TrendingUp className="h-3 w-3 text-green-500" />
                        {reason}
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="text-right">
                  <div className="text-2xl font-bold text-primary">{rec.score}</div>
                  <div className="text-xs text-muted-foreground">promotion score</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* AI Recommendations */}
        {aiRecommendations && (
          <div className="space-y-3 pt-4 border-t">
            <h4 className="font-semibold flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-primary" />
              AI Strategic Analysis
            </h4>
            <div className="p-4 rounded-lg bg-primary/5 border border-primary/20">
              <p className="text-sm whitespace-pre-wrap">{aiRecommendations}</p>
            </div>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={generateAIRecommendations}
              disabled={isGenerating}
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Regenerate
            </Button>
          </div>
        )}

        {(!recommendations.data || recommendations.data.length === 0) && (
          <div className="text-center py-8 text-muted-foreground">
            <Sparkles className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p>No bundle data available for recommendations</p>
            <p className="text-sm">Add bundles and track deals to get promotion suggestions</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
